import React, { useState, useEffect } from 'react';
import { X, ExternalLink, Share2, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../../utils/api';
import DOMPurify from 'dompurify';
import { getConfig } from '../../utils/config';

const API_BASE = (() => {
  const url = getConfig('API_URL') || '';
  return url.replace(/\/api\/v1\/?$/, '');
})();

function resolveUrls(html) {
  if (!html) return '';
  return html.replace(/(src|href)="(\/rails\/[^"]+)"/g, (_, attr, path) => {
    return `${attr}="${API_BASE}${path}"`;
  });
}

export default function ViewNoteModal({ noteId, onClose, theme }) {
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  useEffect(() => {
    if (!noteId) return;
    const load = async () => {
      try {
        const response = await apiGet(`/notes/${noteId}`);
        if (response.ok) {
          const data = await response.json();
          setNote(data.note);
        }
      } catch { /* silent */ }
      setLoading(false);
    };
    load();
  }, [noteId]);

  const safeContent = note?.content
    ? DOMPurify.sanitize(resolveUrls(note.content), { ADD_ATTR: ['target', 'data-attachment-id'] })
    : '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className={`w-full max-w-2xl max-h-[80vh] rounded-xl shadow-xl flex flex-col ${isDark ? 'bg-gray-800' : 'bg-white'}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b flex-shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex-1 min-w-0">
            <h2 className={`font-unna font-semibold text-lg truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {loading ? 'Loading...' : note?.title || 'Untitled'}
            </h2>
            {note?.category && (
              <div className="flex gap-1 mt-1">
                {note.category.split(',').map(c => c.trim()).filter(Boolean).slice(0, 4).map(cat => (
                  <span key={cat} className={`text-[10px] font-rasa px-1.5 py-0.5 rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    {cat}
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-3">
            <button
              onClick={() => {
                onClose();
                navigate(`/dashboard/notes?tab=shared&open=${noteId}`);
              }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-rasa transition-colors ${
                isDark ? 'bg-blue-600/20 hover:bg-blue-600/30 text-blue-300' : 'bg-blue-50 hover:bg-blue-100 text-blue-600'
              }`}
            >
              <ExternalLink size={13} />
              Open in Notes
            </button>
            <button onClick={onClose} className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : note ? (
            <div
              className={`prose prose-base max-w-none font-rasa ${isDark ? 'prose-invert' : ''} [&_img]:rounded-lg [&_img]:max-w-full`}
              dangerouslySetInnerHTML={{ __html: safeContent }}
            />
          ) : (
            <p className={`text-center py-8 font-rasa ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Note not found
            </p>
          )}
        </div>

        {/* Footer */}
        {note && (
          <div className={`flex items-center justify-between px-5 py-3 border-t flex-shrink-0 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            <span className={`text-xs font-rasa ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              {note.owner ? `by ${note.owner.first_name || note.owner.username}` : ''}
              {note.updated_at && ` · ${new Date(note.updated_at).toLocaleDateString()}`}
            </span>
            {note.shared_count > 0 && (
              <span className={`flex items-center gap-1 text-xs font-rasa ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                <Share2 size={11} />
                Shared with {note.shared_count}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
